import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "../../../utils/appToast";
import { Plus, ChevronDown, ChevronUp, Sparkles, Edit3, Bell, Zap, Loader2 } from "lucide-react";
import { getTemplates, getTemplate, getBillingBalance, createTemplate, STUDIO_BILLING_PATH } from "../extractorApi";
import {
  startBuild, selectPages, getStatus, getDraft, patchDraft, saveDraft,
} from "../TemplateBuilder/templateBuilderApi";
import Step2SelectPages from "../TemplateBuilder/Step2SelectPages";
import BuilderLanding from "./BuilderLanding";
import GeneratingProgress from "./GeneratingProgress";
import GenerateReview from "./GenerateReview";
import StudioLibrary from "./StudioLibrary";
import CreateTemplatePanel from "./CreateTemplatePanel";
import Workspace from "./workspace/Workspace";
import {
  errMsg,
  fileFromFormData,
  normalizeTemplateSlug,
  serializeTemplateFields,
} from "./studioConst";
import { SX, FRAME } from "./studioTokens";
import { markOnboarding } from "../../../onboarding/onboardingStore";

/* The two views a URL can address directly (the sidebar links here). Deeper flows
   (workspace, review, aibuild, create) are transient and never in the URL. */
const viewFromParams = (sp) => (sp.get("view") === "library" ? "library" : "landing");

/* Router for Extractor Studio, mirroring the prototype's App: two landing views
   (Builder / Library) plus five full-screen flows that take over the frame —
   manual create, AI build, post-generate review, the template workspace, and
   billing. */
const StudioApp = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [view, setView] = useState(() => viewFromParams(searchParams)); // landing | library | aibuild | create | review | workspace
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [billingBalance, setBillingBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // AI build flow
  const [aiStep, setAiStep] = useState("select"); // select | build
  const [aiDraftId, setAiDraftId] = useState(null);
  const [aiPageCount, setAiPageCount] = useState(null);
  const [aiMaxPages, setAiMaxPages] = useState(null);
  const [aiSelectedPages, setAiSelectedPages] = useState([]);
  const [aiBuildStatus, setAiBuildStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [selectingPages, setSelectingPages] = useState(false);
  const [selectPagesError, setSelectPagesError] = useState(null);
  const aiFileRef = useRef(null);

  // Post-generate review
  const [review, setReview] = useState(null); // { draft, draftId, name, saving }
  const [reviewSaving, setReviewSaving] = useState(false);

  const [ws, setWs] = useState(null);
  const pollingRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    try {
      const data = await getTemplates();
      setTemplates([...(data || [])].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)));
    } finally { setLoadingTemplates(false); }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const loadBillingBalance = useCallback(async () => {
    try {
      const data = await getBillingBalance();
      setBillingBalance(data || null);
    } catch {
      setBillingBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  }, []);
  useEffect(() => { loadBillingBalance(); }, [loadBillingBalance]);

  // Keep in sync with the sidebar's Template Builder / Library links, without
  // disturbing deeper flows (workspace, review, aibuild, create).
  useEffect(() => {
    const qView = viewFromParams(searchParams);
    setView((v) => (v === "landing" || v === "library" ? qView : v));
  }, [searchParams]);

  // Old links (and Stripe returns started before billing moved) still say ?view=billing.
  useEffect(() => {
    if (searchParams.get("view") !== "billing") return;
    const outcome = searchParams.get("billing");
    navigate(`${STUDIO_BILLING_PATH}${outcome ? `&billing=${encodeURIComponent(outcome)}` : ""}`, { replace: true });
  }, [searchParams, navigate]);

  const stopPolling = useCallback(() => { if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } }, []);
  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    if (!newMenuOpen) return;
    const handler = (e) => { if (!e.target.closest("#studio-new-menu")) setNewMenuOpen(false); };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [newMenuOpen]);

  const enterWorkspace = (template, returnView) => {
    stopPolling();
    setWs({ template, templateId: template.id, returnView: returnView || "landing" });
    setReview(null);
    setView("workspace");
  };

  // ── AI generate ──
  const handleGenerate = async (formData) => {
    setGenerating(true); setGenerateError(null);
    try {
      const res = await startBuild(formData);
      aiFileRef.current = fileFromFormData(formData);
      setAiDraftId(res.draft_id); setAiPageCount(res.page_count); setAiMaxPages(res.max_selectable_pages);
      setAiSelectedPages([]); setAiStep("select"); setView("aibuild");
    } catch (err) { setGenerateError(errMsg(err, "Failed to start build. Check your file and try again.")); }
    finally { setGenerating(false); }
  };

  const resetAi = () => {
    stopPolling(); aiFileRef.current = null;
    setAiDraftId(null); setAiPageCount(null); setAiMaxPages(null);
    setAiSelectedPages([]); setAiBuildStatus(null); setAiStep("select");
    setReview(null); setView("landing");
  };

  const startPolling = useCallback((id) => {
    stopPolling();
    const poll = async () => {
      try {
        const status = await getStatus(id);
        setAiBuildStatus(status);
        if (status.status === "ready") {
          stopPolling();
          try {
            const draft = await getDraft(id);
            // The prototype drops straight into a review of what the AI proposed
            // before anything is persisted, so the user commits deliberately.
            setReview({ draft, draftId: id });
            setView("review");
          } catch (e) {
            if (e?.response?.status === 410) { toast.error("Session expired. Please start over."); resetAi(); }
            else setAiBuildStatus((p) => ({ ...p, status: "error", error: "Failed to load draft results." }));
          }
        } else if (status.status === "error") { stopPolling(); }
      } catch (e) {
        if (e?.response?.status === 410) { stopPolling(); toast.error("Session expired. Please start over."); resetAi(); }
      }
    };
    poll();
    pollingRef.current = setInterval(poll, 2500);
  }, [stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectPages = async () => {
    setSelectingPages(true); setSelectPagesError(null);
    try { await selectPages(aiDraftId, { selected_pages: aiSelectedPages }); setAiStep("build"); startPolling(aiDraftId); }
    catch (err) { setSelectPagesError(errMsg(err, "Failed to select pages. Please try again.")); }
    finally { setSelectingPages(false); }
  };
  const handleRetryBuild = () => { if (!aiDraftId) { resetAi(); return; } setAiBuildStatus(null); setAiStep("build"); startPolling(aiDraftId); };

  // ── Review-step actions ──
  const handleReviewToggle = async (fieldKey, enabled) => {
    const prev = review.draft.fields;
    setReview((r) => ({ ...r, draft: { ...r.draft, fields: r.draft.fields.map((f) => f.field_key === fieldKey ? { ...f, enabled } : f) } }));
    try {
      const res = await patchDraft(review.draftId, { fields: [{ field_key: fieldKey, enabled }] });
      setReview((r) => (r ? { ...r, draft: { ...r.draft, fields: res.fields } } : r));
    } catch (err) {
      setReview((r) => (r ? { ...r, draft: { ...r.draft, fields: prev } } : r));
      toast.error(errMsg(err, "Failed to update field."));
    }
  };

  const handleReviewSave = async () => {
    const name = (review.name || "").trim();
    if (!name) { toast.error("Give the template a name first."); return; }
    setReviewSaving(true);
    try {
      const d = review.draft;
      const res = await saveDraft(review.draftId, {
        name,
        document_type: d.document_type === "auto" ? "custom" : d.document_type,
        extraction_engine: d.engine || "standard_layout",
        retrieval_strategy: d.retrieval_strategy || "full_text",
        llm_model_key: d.model || "gemini_2_5_flash_lite",
      });
      toast.success("Template saved.");
      markOnboarding("template");
      loadTemplates();
      // Once the AI draft is saved, the workspace starts from the persisted
      // template instead of carrying the temporary draft forward.
      const templateId = res.template?.id;
      if (templateId) {
        const saved = await getTemplate(templateId);
        enterWorkspace(saved, "landing");
      } else {
        resetAi();
      }
    } catch (err) { toast.error(errMsg(err, "Failed to save template.")); }
    finally { setReviewSaving(false); }
  };

  // ── Reopen ──
  const openTemplate = async (tpl) => {
    const returnView = view === "library" ? "library" : "landing";
    const t = toast.loading("Opening template…");
    try {
      const template = await getTemplate(tpl.id);
      toast.dismiss(t); enterWorkspace(template, returnView);
    } catch (err) { toast.dismiss(t); toast.error(errMsg(err, "Couldn't open that template.")); }
  };

  // ── Manual create ──
  const handleManualCreate = async ({ name, document_type, engine, fields }) => {
    setCreating(true);
    try {
      const slug = normalizeTemplateSlug(name) || `template-${Date.now()}`;
      const created = await createTemplate({
        name,
        slug,
        document_type,
        extraction_engine: engine,
        retrieval_strategy: "full_text",
        llm_model_key: "gemini_2_5_flash_lite",
        fields: serializeTemplateFields(fields),
      });
      const createdTemplate = created.template || created;
      if (!createdTemplate.id) throw new Error("The template was created without an id.");
      const template = await getTemplate(createdTemplate.id);
      enterWorkspace(template, "landing");
    } catch (err) { toast.error(errMsg(err, "Couldn't create the template.")); }
    finally { setCreating(false); }
  };

  const handleSaved = () => loadTemplates();
  const handleBack = () => { const back = ws?.returnView || "landing"; setWs(null); setView(back); loadTemplates(); };

  const monthlyCredits = Number(billingBalance?.allocation_credits || billingBalance?.monthly_credits || 0);
  const remainingCredits = Number(billingBalance?.balance ?? billingBalance?.credits ?? 0);
  const hasBillingData = Boolean(billingBalance && monthlyCredits > 0);
  const usedCredits = billingBalance?.used_this_period != null
    ? Number(billingBalance.used_this_period)
    : Math.max(monthlyCredits - remainingCredits, 0);
  const usagePercent = monthlyCredits > 0 ? Math.min((usedCredits / monthlyCredits) * 100, 100) : 0;
  const planLabel = billingBalance?.plan_name || "Current plan";

  // The meter doubles as the entry point to the billing page — there's no other
  // nav chrome in Studio, and credits are what a user clicks when they want to
  // know about their plan.
  const PricingMeter = () => (
    <button
      type="button"
      onClick={() => navigate(STUDIO_BILLING_PATH)}
      title="View billing & usage"
      className="h-11 inline-flex items-center gap-3 px-4 rounded-full border border-[#ECE9EE] bg-white text-left shadow-[0_1px_2px_rgba(16,24,40,0.02)] transition-colors hover:border-[#E9D2E6] hover:bg-[#FBF5FA] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E9D2E6]"
    >
      <Zap size={14} className="text-[#A04A97] flex-shrink-0" />
      <div className="w-20 h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden flex-shrink-0">
        <div
          className="h-full rounded-full bg-[#A04A97] transition-all duration-300"
          style={{ width: `${hasBillingData ? usagePercent : 0}%` }}
        />
      </div>
      <span className="text-[13px] font-semibold text-[#1A1A1A] tabular-nums">
        {loadingBalance ? (
          <span className="inline-flex items-center gap-1.5 text-[#52525B]">
            <Loader2 size={12} className="animate-spin" />
            Loading
          </span>
        ) : hasBillingData ? (
          `${Math.round(usagePercent)}%`
        ) : (
          "--"
        )}
      </span>
      <span className="text-[13px] font-semibold text-[#6F6F76] truncate max-w-[96px]">{planLabel}</span>
    </button>
  );

  // ── Full-screen flows (own top bar) ──
  if (view === "workspace" && ws) {
    return (
      <Workspace key={ws.templateId} template={ws.template}
        onBack={handleBack} onSaved={handleSaved} />
    );
  }
  if (view === "create") {
    return <CreateTemplatePanel onClose={() => setView("landing")} onCreate={handleManualCreate} busy={creating} />;
  }
  if (view === "review" && review) {
    return (
      <GenerateReview
        draft={review.draft}
        templateName={review.name || ""}
        onNameChange={(v) => setReview((r) => ({ ...r, name: v }))}
        onToggleField={handleReviewToggle}
        onSave={handleReviewSave}
        onStartOver={resetAi}
        saving={reviewSaving}
      />
    );
  }

  const recent = templates.slice(0, 3);

  const NewMenuItem = ({ id, icon: Ico, label, desc }) => (
    <div className="flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer hover:bg-[#F5F5F5]"
      onClick={() => { setNewMenuOpen(false); if (id === "ai") setView("landing"); else setView("create"); }}>
      <div className="w-7 h-7 rounded-lg bg-[#FBF5FA] flex items-center justify-center flex-shrink-0 text-[#A04A97]"><Ico size={13} /></div>
      <div>
        <div className="text-[13px] font-semibold text-[#1A1A1A]">{label}</div>
        <div className="text-[11.5px] text-[#52525B] mt-0.5 leading-[1.35]">{desc}</div>
      </div>
    </div>
  );

  const pageTitle = view === "library" ? "Template Library" : "Template Builder";

  return (
    <div className="flex flex-col h-full min-h-0 font-outfit">
      <div className={`${SX.topbar} !h-auto min-h-16 flex-wrap gap-3 px-4 py-3 sm:px-7 sm:flex-nowrap sm:py-0 justify-between`}>
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="text-[20px] font-semibold text-[#1A1A1A] truncate">{pageTitle}</div>
        </div>
        <div className="flex items-center gap-2.5 min-w-0 flex-wrap justify-end">
          <PricingMeter />
          <div className="relative" id="studio-new-menu">
            <button className={`${SX.btn} ${SX.btnPrimary}`}
              onClick={(e) => { e.stopPropagation(); setNewMenuOpen((o) => !o); }}>
              <Plus size={14} /> New Template {newMenuOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {newMenuOpen && (
              <div className={`absolute top-12 right-0 w-[260px] p-1.5 z-[60] ${SX.popover}`}>
                <NewMenuItem id="ai" icon={Sparkles} label="Create with AI" desc="Generate a template from a sample document" />
                <NewMenuItem id="manual" icon={Edit3} label="Create Manually" desc="Build the schema field by field" />
              </div>
            )}
          </div>
          <button className="w-10 h-10 flex items-center justify-center bg-white border border-[#ECE9EE] rounded-[9px] text-[#52525B] hover:bg-[#F5F5F5] hover:text-[#1A1A1A] transition-colors"><Bell size={17} /></button>
        </div>
      </div>

      {view === "landing" && (
        <BuilderLanding onGenerate={handleGenerate} isGenerating={generating} generateError={generateError}
          recent={recent} onOpenTemplate={openTemplate} onOpenLibrary={() => setView("library")}
          onManual={() => setView("create")} />
      )}
      {view === "library" && (
        <StudioLibrary
          templates={templates}
          loading={loadingTemplates}
          billingBalance={billingBalance}
          loadingBalance={loadingBalance}
          onOpenTemplate={openTemplate}
          onManageUsage={() => navigate(STUDIO_BILLING_PATH)}
        />
      )}
      {view === "aibuild" && (
        <div className={`flex-1 overflow-hidden flex flex-col ${FRAME.canvas}`}>
          {aiStep === "select" && (
            <Step2SelectPages pageCount={aiPageCount} maxSelectablePages={aiMaxPages} selectedPages={aiSelectedPages}
              onSelectedPagesChange={setAiSelectedPages} onSubmit={handleSelectPages} isSubmitting={selectingPages}
              error={selectPagesError} onCancel={resetAi} />
          )}
          {aiStep === "build" && <GeneratingProgress buildStatus={aiBuildStatus} onRetry={handleRetryBuild} onCancel={resetAi} />}
        </div>
      )}
    </div>
  );
};

export default StudioApp;
